import { RumEvent } from '@datadog/browser-rum';

type RumErrorEvent = RumEvent & { error?: { message?: string; stack?: string; resource?: { url?: string } } };

/**
 * Interim noise filter and scrubber for RUM error events.
 *
 * Unused Google Ads tags (conversion ID 984292306, GTM container GTM-NS64KQZ) are blocked by our CSP and send
 * most CSP reports, some with a hashed email in the query string. Only these Ads endpoints are dropped, so every
 * other CSP violation still surfaces: an unexpected blocked script can indicate XSS. Remove them once marketing
 * removes the tags from GTM.
 */
export const IGNORED_CSP_VIOLATION_TARGETS: ReadonlyArray<{ host: string; pathPrefix: string }> = [
    { host: 'www.google.com', pathPrefix: '/rmkt/' },
    { host: 'www.google.com', pathPrefix: '/pagead/' },
    { host: 'www.google.com', pathPrefix: '/ccm/' },
    { host: 'google.com', pathPrefix: '/pagead/' },
    { host: 'google.com', pathPrefix: '/ccm/' },
    { host: 'ad.doubleclick.net', pathPrefix: '/' },
    { host: 'googleads.g.doubleclick.net', pathPrefix: '/' }
];

/** Benign: a resize callback changed layout again. */
const IGNORED_ERROR_MESSAGES = [
    'ResizeObserver loop completed with undelivered notifications.',
    'ResizeObserver loop limit exceeded'
];

// The SDK reports CSP violations as: csp_violation: '<blocked URI>' blocked by '<directive>' directive
const CSP_BLOCKED_URI = /^(csp_violation: ')([^']*)(')/;
// Bounded quantifiers (RFC 5321 lengths) keep matching linear on long messages and stacks.
const EMAIL = /[\w.+-]{1,64}(?:@|%40)[\w-]{1,63}(?:\.[\w-]{1,63}){0,8}\.[a-z]{2,24}/gi;

function isIgnoredCspTarget(blockedUri: string): boolean {
    let url: URL;
    try {
        url = new URL(blockedUri);
    } catch {
        return false;
    }
    // Parsed hostname: browsers report e.g. 'https://www.google.com:443/...'.
    return IGNORED_CSP_VIOLATION_TARGETS.some(
        target => url.hostname === target.host && url.pathname.startsWith(target.pathPrefix)
    );
}

/**
 * Returns true when a RUM event should be dropped (not sent to Datadog).
 */
export function shouldDiscardRumEvent(event: RumEvent): boolean {
    if (event.type !== 'error') {
        return false;
    }

    const message = (event as RumErrorEvent).error?.message;
    if (typeof message !== 'string') {
        return false;
    }

    const blockedUri = CSP_BLOCKED_URI.exec(message)?.[2];
    if (blockedUri !== undefined) {
        return isIgnoredCspTarget(blockedUri);
    }

    // Uncaught errors arrive as: Uncaught "<message>"
    return IGNORED_ERROR_MESSAGES.some(ignored => message === `Uncaught "${ignored}"` || message === ignored);
}

/** In place: drops CSP report URIs' query and fragment, and masks email-shaped text. */
export function scrubRumErrorEvent(event: RumEvent): void {
    const error = (event as RumErrorEvent).error;
    if (event.type !== 'error' || !error) {
        return;
    }

    if (typeof error.message === 'string') {
        error.message = error.message
            .replace(CSP_BLOCKED_URI, (_, start, uri, end) => `${start}${uri.split(/[?#]/)[0]}${end}`)
            .replace(EMAIL, '_');
    }
    if (typeof error.stack === 'string') {
        error.stack = error.stack.replace(EMAIL, '_');
    }
    if (typeof error.resource?.url === 'string') {
        error.resource.url = error.resource.url.replace(EMAIL, '_');
    }
}
